import React, { useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import './TTSComparePage.css';

const VOICE_OPTIONS = [
  'Dennis', 'Ashley', 'Brian', 'Jessica', 'Evan', 'Sarah',
  'Derek', 'Grant', 'Oliver', 'Kelsey', 'Kira', 'Elias', 'Lily', 'Marcus',
];

const TEMP_VALUES = [0.1, 0.3, 0.5, 0.7, 0.8, 0.9, 1.0, 1.2, 1.5];

interface SentenceDef {
  label: string;
  type: string;
  text: string;
  voice: string;
  speaker: string;
  // Pitch analysis results per temperature
  analysis?: Record<string, {
    intonationRank: number;  // 1=best rising intonation
    bestRank: number;        // 1=best overall
    riseHz: number;
    ratio: number;
    intonationScore: number;
    combinedScore: number;
  }>;
  bestIntonationTemp?: number;
  bestOverallTemp?: number;
}

// Y/N Questions from Asking for Directions (2dda73ca) with pitch analysis results
const COURSE_SENTENCES: SentenceDef[] = [
  {
    label: 'Yes/No Q', type: 'question-yn',
    text: 'Do I turn left?', voice: 'Brian', speaker: 'Tom',
    bestIntonationTemp: 0.9, bestOverallTemp: 0.9,
    analysis: {
      '0.1': { intonationRank: 2, bestRank: 4, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 5.1 },
      '0.3': { intonationRank: 8, bestRank: 8, riseHz: -17.7, ratio: 0.826, intonationScore: -22.1, combinedScore: -6.6 },
      '0.5': { intonationRank: 3, bestRank: 6, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 4.3 },
      '0.7': { intonationRank: 9, bestRank: 9, riseHz: -30.9, ratio: 0.741, intonationScore: -87.2, combinedScore: -51.3 },
      '0.8': { intonationRank: 4, bestRank: 3, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 8.3 },
      '0.9': { intonationRank: 1, bestRank: 1, riseHz: 1427.6, ratio: 1.866, intonationScore: 115.2, combinedScore: 81.9 },
      '1.0': { intonationRank: 5, bestRank: 7, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 2.8 },
      '1.2': { intonationRank: 6, bestRank: 2, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 10.5 },
      '1.5': { intonationRank: 7, bestRank: 5, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 5.0 },
    },
  },
  {
    label: 'Yes/No Q', type: 'question-yn',
    text: 'Turn right at the corner?', voice: 'Brian', speaker: 'Tom',
    bestIntonationTemp: 0.8, bestOverallTemp: 0.8,
    analysis: {
      '0.1': { intonationRank: 4, bestRank: 4, riseHz: 361.2, ratio: 1.243, intonationScore: 31.5, combinedScore: 27.5 },
      '0.3': { intonationRank: 5, bestRank: 6, riseHz: 2.3, ratio: 1.011, intonationScore: 24.7, combinedScore: 18.7 },
      '0.5': { intonationRank: 9, bestRank: 9, riseHz: 0.7, ratio: 1.003, intonationScore: 7.0, combinedScore: 6.1 },
      '0.7': { intonationRank: 6, bestRank: 5, riseHz: 4.0, ratio: 1.023, intonationScore: 24.6, combinedScore: 18.8 },
      '0.8': { intonationRank: 1, bestRank: 1, riseHz: 87.2, ratio: 1.664, intonationScore: 324.0, combinedScore: 221.8 },
      '0.9': { intonationRank: 8, bestRank: 8, riseHz: 2.7, ratio: 1.012, intonationScore: 19.3, combinedScore: 13.6 },
      '1.0': { intonationRank: 7, bestRank: 7, riseHz: 2.6, ratio: 1.015, intonationScore: 21.3, combinedScore: 15.9 },
      '1.2': { intonationRank: 2, bestRank: 2, riseHz: 64.8, ratio: 1.511, intonationScore: 187.3, combinedScore: 131.2 },
      '1.5': { intonationRank: 3, bestRank: 3, riseHz: 3.6, ratio: 1.034, intonationScore: 48.8, combinedScore: 32.8 },
    },
  },
  {
    label: 'Yes/No Q', type: 'question-yn',
    text: 'Is it far from here?', voice: 'Brian', speaker: 'Tom',
    bestIntonationTemp: 1.2, bestOverallTemp: 1.2,
    analysis: {
      '0.1': { intonationRank: 2, bestRank: 2, riseHz: 81.5, ratio: 1.686, intonationScore: 227.6, combinedScore: 158.2 },
      '0.3': { intonationRank: 9, bestRank: 9, riseHz: -64.1, ratio: 0.508, intonationScore: -115.7, combinedScore: -64.5 },
      '0.5': { intonationRank: 7, bestRank: 7, riseHz: 56.6, ratio: 1.370, intonationScore: 148.4, combinedScore: 106.5 },
      '0.7': { intonationRank: 5, bestRank: 5, riseHz: 58.9, ratio: 1.418, intonationScore: 163.8, combinedScore: 115.3 },
      '0.8': { intonationRank: 6, bestRank: 6, riseHz: 58.2, ratio: 1.309, intonationScore: 153.6, combinedScore: 108.9 },
      '0.9': { intonationRank: 3, bestRank: 3, riseHz: 68.4, ratio: 1.481, intonationScore: 202.6, combinedScore: 141.3 },
      '1.0': { intonationRank: 8, bestRank: 8, riseHz: 41.6, ratio: 1.277, intonationScore: 102.2, combinedScore: 76.6 },
      '1.2': { intonationRank: 1, bestRank: 1, riseHz: 107.6, ratio: 1.782, intonationScore: 277.4, combinedScore: 191.5 },
      '1.5': { intonationRank: 4, bestRank: 4, riseHz: 74.3, ratio: 1.517, intonationScore: 183.8, combinedScore: 129.7 },
    },
  },
  {
    label: 'Yes/No Q', type: 'question-yn',
    text: 'Can I walk there?', voice: 'Brian', speaker: 'Tom',
    bestIntonationTemp: 0.9, bestOverallTemp: 0.9,
    analysis: {
      '0.1': { intonationRank: 4, bestRank: 4, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 8.4 },
      '0.3': { intonationRank: 5, bestRank: 9, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 2.0 },
      '0.5': { intonationRank: 3, bestRank: 3, riseHz: 151.5, ratio: 1.098, intonationScore: 12.8, combinedScore: 15.3 },
      '0.7': { intonationRank: 6, bestRank: 7, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 3.7 },
      '0.8': { intonationRank: 2, bestRank: 2, riseHz: 173.1, ratio: 1.127, intonationScore: 16.2, combinedScore: 17.5 },
      '0.9': { intonationRank: 1, bestRank: 1, riseHz: 61.6, ratio: 1.588, intonationScore: 164.1, combinedScore: 115.7 },
      '1.0': { intonationRank: 7, bestRank: 6, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 5.6 },
      '1.2': { intonationRank: 8, bestRank: 8, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 2.9 },
      '1.5': { intonationRank: 9, bestRank: 5, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 6.1 },
    },
  },
  {
    label: 'Yes/No Q', type: 'question-yn',
    text: 'Is the bank near there?', voice: 'Brian', speaker: 'Tom',
    bestIntonationTemp: 0.9, bestOverallTemp: 0.9,
    analysis: {
      '0.1': { intonationRank: 7, bestRank: 7, riseHz: -38.9, ratio: 0.708, intonationScore: -55.1, combinedScore: -26.1 },
      '0.3': { intonationRank: 6, bestRank: 6, riseHz: 0, ratio: 1.0, intonationScore: 0, combinedScore: 11.2 },
      '0.5': { intonationRank: 8, bestRank: 8, riseHz: -46.1, ratio: 0.585, intonationScore: -125.0, combinedScore: -70.8 },
      '0.7': { intonationRank: 4, bestRank: 4, riseHz: 47.4, ratio: 1.342, intonationScore: 109.2, combinedScore: 79.7 },
      '0.8': { intonationRank: 2, bestRank: 2, riseHz: 89.4, ratio: 1.459, intonationScore: 211.5, combinedScore: 148.4 },
      '0.9': { intonationRank: 1, bestRank: 1, riseHz: 134.8, ratio: 2.366, intonationScore: 353.6, combinedScore: 241.0 },
      '1.0': { intonationRank: 9, bestRank: 9, riseHz: -57.8, ratio: 0.535, intonationScore: -126.6, combinedScore: -71.1 },
      '1.2': { intonationRank: 5, bestRank: 5, riseHz: 35.8, ratio: 1.215, intonationScore: 106.5, combinedScore: 78.3 },
      '1.5': { intonationRank: 3, bestRank: 3, riseHz: 70.4, ratio: 1.518, intonationScore: 186.3, combinedScore: 130.5 },
    },
  },
];

const TYPE_COLORS: Record<string, string> = {
  'question-yn': '#ef4444',
  'custom': '#6b7280',
};

const VOICE_COLORS: Record<string, string> = {
  'Brian': '#0891b2', 'Jessica': '#d946ef', 'Dennis': '#0891b2',
  'Ashley': '#d946ef', 'Evan': '#059669', 'Sarah': '#e11d48',
  'Derek': '#7c3aed', 'Grant': '#ca8a04', 'Oliver': '#475569', 'Kelsey': '#f97316',
};

const TYPE_LABELS: Record<string, string> = {
  'question-yn': 'Yes/No Q',
  'custom': 'Custom',
};

interface GeneratedAudio {
  audioUrl: string;
  latencyMs: number;
  cached: boolean;
}

interface Sentence {
  label: string;
  type: string;
  text: string;
  voice?: string;
  speaker?: string;
  analysis?: SentenceDef['analysis'];
  bestIntonationTemp?: number;
  bestOverallTemp?: number;
}

type ResultMap = Record<string, GeneratedAudio>;

// Badge for ranking
const RankBadge: React.FC<{ rank: number; type: 'intonation' | 'overall' }> = ({ rank, type }) => {
  if (rank > 3) return null;
  const colors = type === 'intonation'
    ? { 1: '#dc2626', 2: '#ea580c', 3: '#d97706' }
    : { 1: '#2563eb', 2: '#4f46e5', 3: '#7c3aed' };
  const labels = { 1: '#1', 2: '#2', 3: '#3' } as Record<number, string>;
  return (
    <span style={{
      position: 'absolute', top: -4, right: type === 'intonation' ? -4 : 12,
      fontSize: 8, fontWeight: 700, color: '#fff',
      background: colors[rank as 1|2|3], borderRadius: 6,
      padding: '0 3px', lineHeight: '14px', minWidth: 14, textAlign: 'center',
      border: '1px solid #fff',
    }}>
      {labels[rank]}
    </span>
  );
};

const TTSTemperaturePage: React.FC = () => {
  const [voiceId, setVoiceId] = useState('Brian');
  const [speakingRate, setSpeakingRate] = useState(1.0);
  const [sentences, setSentences] = useState<Sentence[]>(COURSE_SENTENCES.map(p => ({ ...p })));
  const [customText, setCustomText] = useState('');
  const [results, setResults] = useState<ResultMap>({});
  const [generating, setGenerating] = useState<Record<string, boolean>>({});
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [showRank, setShowRank] = useState<'intonation' | 'overall' | 'none'>('intonation');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = useCallback((url: string, key: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingAudio === key) {
      setPlayingAudio(null);
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const audio = new Audio(fullUrl);
    audioRef.current = audio;
    setPlayingAudio(key);
    audio.onended = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.onerror = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.play().catch(() => { setPlayingAudio(null); audioRef.current = null; });
  }, [playingAudio]);

  const generateOne = useCallback(async (sentenceIdx: number, temp: number) => {
    const key = `${sentenceIdx}_${temp}`;
    const sent = sentences[sentenceIdx];
    const useVoice = sent.voice || voiceId;
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/dictionary/tts-sentence/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sent.text,
          voiceId: useVoice,
          speakingRate,
          temperature: temp,
        }),
      });
      const data = await res.json();
      if (data.audioUrl) {
        setResults(prev => ({
          ...prev,
          [key]: { audioUrl: data.audioUrl, latencyMs: data.latencyMs, cached: data.cached },
        }));
      }
    } catch (err) {
      console.error('TTS generation failed:', err);
    } finally {
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  }, [sentences, voiceId, speakingRate]);

  const generateRow = useCallback(async (sentenceIdx: number) => {
    for (const temp of TEMP_VALUES) {
      const key = `${sentenceIdx}_${temp}`;
      if (!results[key]) {
        await generateOne(sentenceIdx, temp);
      }
    }
  }, [results, generateOne]);

  const generateAll = useCallback(async () => {
    setBatchRunning(true);
    for (let i = 0; i < sentences.length; i++) {
      for (const temp of TEMP_VALUES) {
        const key = `${i}_${temp}`;
        if (!results[key]) {
          await generateOne(i, temp);
        }
      }
    }
    setBatchRunning(false);
  }, [sentences, results, generateOne]);

  const addCustomSentence = useCallback(() => {
    const text = customText.trim();
    if (!text) return;
    setSentences(prev => [...prev, { label: 'Custom', type: 'custom', text }]);
    setCustomText('');
  }, [customText]);

  const updateSentenceVoice = useCallback((idx: number, voice: string) => {
    setSentences(prev => prev.map((s, i) => i === idx ? { ...s, voice } : s));
    setResults(prev => {
      const next = { ...prev };
      for (const temp of TEMP_VALUES) {
        delete next[`${idx}_${temp}`];
      }
      return next;
    });
  }, []);

  const clearResults = useCallback(() => {
    setResults({});
  }, []);

  // Get cell highlight color based on ranking
  const getCellBg = (sent: Sentence, temp: number): string => {
    if (showRank === 'none' || !sent.analysis) return 'transparent';
    const a = sent.analysis[String(temp)];
    if (!a) return 'transparent';
    const rank = showRank === 'intonation' ? a.intonationRank : a.bestRank;
    if (rank === 1) return showRank === 'intonation' ? '#fef2f2' : '#eff6ff';
    if (rank === 2) return showRank === 'intonation' ? '#fff7ed' : '#eef2ff';
    if (rank === 3) return showRank === 'intonation' ? '#fffbeb' : '#f5f3ff';
    return 'transparent';
  };

  return (
    <div className="tts-compare-page" style={{ maxWidth: 1400 }}>
      <div className="tts-compare-header">
        <h1>Y/N Question Intonation Test</h1>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 8px' }}>
          Asking for Directions (2dda73ca) &middot; 5 Yes/No questions &times; 9 temperatures &middot; Pitch analysis via librosa pyin
        </p>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6b7280', margin: '4px 0 12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <strong>Show rankings:</strong>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" checked={showRank === 'intonation'} onChange={() => setShowRank('intonation')} />
            <span style={{ color: '#dc2626' }}>Rising Intonation</span> (F0 pitch rise at sentence end)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" checked={showRank === 'overall'} onChange={() => setShowRank('overall')} />
            <span style={{ color: '#2563eb' }}>Best Overall</span> (intonation + quality)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" checked={showRank === 'none'} onChange={() => setShowRank('none')} />
            None
          </label>
        </div>

        <div className="tts-compare-controls">
          <label>
            Default Voice:
            <select value={voiceId} onChange={e => { setVoiceId(e.target.value); clearResults(); }}>
              {VOICE_OPTIONS.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="tts-slider-label">
            Rate: {speakingRate.toFixed(2)}
            <input
              type="range"
              className="tts-slider"
              min={0.8}
              max={1.2}
              step={0.05}
              value={speakingRate}
              onChange={e => { setSpeakingRate(parseFloat(e.target.value)); clearResults(); }}
            />
          </label>
          <button
            className="tts-compare-btn tts-compare-btn--green"
            onClick={generateAll}
            disabled={batchRunning || sentences.length === 0}
          >
            {batchRunning ? 'Generating...' : 'Generate All'}
          </button>
          <button
            className="tts-compare-btn"
            style={{ background: '#e5e7eb', color: '#374151' }}
            onClick={clearResults}
          >
            Clear
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <input
            type="text"
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustomSentence()}
            placeholder="Add a custom question to test (e.g. 'Is the restaurant open?')"
            style={{
              flex: 1, padding: '8px 12px', border: '1px solid #d1d5db',
              borderRadius: 6, fontSize: 14, outline: 'none',
            }}
          />
          <button
            className="tts-compare-btn tts-compare-btn--primary"
            onClick={addCustomSentence}
            disabled={!customText.trim()}
          >
            Add
          </button>
        </div>
      </div>

      {/* Temperature grid */}
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', minWidth: 300, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                Sentence
              </th>
              {TEMP_VALUES.map(t => (
                <th key={t} style={{ padding: '8px 6px', textAlign: 'center', minWidth: 64, whiteSpace: 'nowrap' }}>
                  T={t}
                </th>
              ))}
              <th style={{ padding: '8px 6px', minWidth: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {sentences.map((s, idx) => {
              const effectiveVoice = s.voice || voiceId;
              return (
              <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 12px', position: 'sticky', left: 0, background: '#fff', zIndex: 1, minWidth: 300 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                      fontSize: 10, fontWeight: 600, color: '#fff',
                      background: TYPE_COLORS[s.type] || '#6b7280',
                    }}>
                      {s.label}
                    </span>
                    {s.speaker && (
                      <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 500 }}>{s.speaker}</span>
                    )}
                    <select
                      value={effectiveVoice}
                      onChange={e => updateSentenceVoice(idx, e.target.value)}
                      style={{
                        fontSize: 10, padding: '1px 4px', borderRadius: 4,
                        border: '1px solid #d1d5db', background: '#f9fafb',
                        color: VOICE_COLORS[effectiveVoice] || '#374151',
                        fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {VOICE_OPTIONS.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    {s.bestIntonationTemp != null && (
                      <span style={{
                        fontSize: 9, padding: '1px 5px', borderRadius: 4,
                        background: '#fef2f2', color: '#dc2626', fontWeight: 600, border: '1px solid #fecaca',
                      }}>
                        Best: T={s.bestIntonationTemp}
                      </span>
                    )}
                  </div>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{s.text}</span>
                </td>
                {TEMP_VALUES.map(temp => {
                  const key = `${idx}_${temp}`;
                  const result = results[key];
                  const isGen = generating[key];
                  const isPlaying = playingAudio === key;
                  const cellBg = getCellBg(s, temp);
                  const a = s.analysis?.[String(temp)];
                  const intoRank = a?.intonationRank;
                  const bestRank = a?.bestRank;
                  return (
                    <td key={temp} style={{ padding: '6px', textAlign: 'center', background: cellBg, position: 'relative' }}>
                      {result ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            onClick={() => playAudio(result.audioUrl, key)}
                            style={{
                              width: 36, height: 36, borderRadius: '50%',
                              border: isPlaying ? '2px solid #6366f1' : '1px solid #d1d5db',
                              background: isPlaying ? '#6366f1' : '#f9fafb',
                              cursor: 'pointer', display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s',
                            }}
                            title={[
                              result.cached ? 'cached' : `${result.latencyMs}ms`,
                              a ? `Rise: ${a.riseHz > 0 ? '+' : ''}${a.riseHz.toFixed(0)}Hz` : '',
                              a ? `Ratio: ${a.ratio.toFixed(2)}` : '',
                              intoRank ? `Intonation #${intoRank}` : '',
                              bestRank ? `Overall #${bestRank}` : '',
                            ].filter(Boolean).join('\n')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24"
                              fill={isPlaying ? '#fff' : '#374151'} stroke="none">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </button>
                          {showRank === 'intonation' && intoRank && <RankBadge rank={intoRank} type="intonation" />}
                          {showRank === 'overall' && bestRank && <RankBadge rank={bestRank} type="overall" />}
                        </div>
                      ) : isGen ? (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>...</span>
                      ) : (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            onClick={() => generateOne(idx, temp)}
                            disabled={batchRunning}
                            style={{
                              width: 36, height: 36, borderRadius: '50%',
                              border: '1px dashed #d1d5db', background: 'transparent',
                              cursor: 'pointer', fontSize: 16, color: '#9ca3af',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            title={a ? `Rise: ${a.riseHz > 0 ? '+' : ''}${a.riseHz.toFixed(0)}Hz, Ratio: ${a.ratio.toFixed(2)}` : 'Generate'}
                          >
                            +
                          </button>
                          {showRank === 'intonation' && intoRank && <RankBadge rank={intoRank} type="intonation" />}
                          {showRank === 'overall' && bestRank && <RankBadge rank={bestRank} type="overall" />}
                        </div>
                      )}
                      {/* Rise info below button */}
                      {a && a.riseHz !== 0 && (
                        <div style={{
                          fontSize: 9, marginTop: 2,
                          color: a.riseHz > 0 ? '#16a34a' : '#dc2626',
                          fontWeight: 500,
                        }}>
                          {a.riseHz > 0 ? '+' : ''}{a.riseHz.toFixed(0)}Hz
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{ padding: '6px' }}>
                  <button
                    className="tts-compare-btn tts-compare-btn--primary"
                    style={{ fontSize: 11, padding: '4px 8px' }}
                    onClick={() => generateRow(idx)}
                    disabled={batchRunning}
                  >
                    Row
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{ margin: '20px 0', padding: '16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Pitch Analysis Summary (librosa pyin)</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Temperature</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>Avg Combined Score</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>Intonation Wins</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>Overall Wins</th>
            </tr>
          </thead>
          <tbody>
            {[
              { t: 0.9, avg: 118.7, iWins: 3, oWins: 3 },
              { t: 0.8, avg: 101.0, iWins: 1, oWins: 1 },
              { t: 1.2, avg: 82.9, iWins: 1, oWins: 1 },
              { t: 1.5, avg: 60.8, iWins: 0, oWins: 0 },
              { t: 0.1, avg: 34.6, iWins: 0, oWins: 0 },
              { t: 0.7, avg: 33.2, iWins: 0, oWins: 0 },
              { t: 0.5, avg: 12.3, iWins: 0, oWins: 0 },
              { t: 1.0, avg: 6.0, iWins: 0, oWins: 0 },
              { t: 0.3, avg: -7.8, iWins: 0, oWins: 0 },
            ].map(row => (
              <tr key={row.t} style={{
                borderBottom: '1px solid #f1f5f9',
                background: row.t === 0.9 ? '#f0fdf4' : row.t === 0.8 ? '#fefce8' : 'transparent',
                fontWeight: row.iWins > 0 ? 600 : 400,
              }}>
                <td style={{ padding: '6px 8px' }}>T={row.t}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', color: row.avg > 0 ? '#16a34a' : '#dc2626' }}>
                  {row.avg > 0 ? '+' : ''}{row.avg.toFixed(1)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  {row.iWins > 0 ? `${row.iWins}/5` : '-'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  {row.oWins > 0 ? `${row.oWins}/5` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>
          <strong>Conclusion:</strong> T=0.9 is the best temperature for Y/N question rising intonation (wins 3/5 questions).
          T=0.8 and T=1.2 each win 1 question. Low temperatures (0.1-0.5) and T=1.0 tend to produce flat or falling pitch at sentence end.
        </div>
      </div>

      <div className="tts-compare-footer" style={{ marginTop: 8 }}>
        <div className="tts-compare-stats">
          <span>Voice: <strong>{voiceId}</strong></span>
          <span>Rate: <strong>{speakingRate.toFixed(2)}</strong></span>
          <span>Generated: <strong>{Object.keys(results).length}</strong> / {sentences.length * TEMP_VALUES.length}</span>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
          Hover over play buttons to see pitch rise (Hz) and ranking. Green Hz = rising intonation, Red Hz = falling.
          Rankings are from F0 pitch contour analysis (last 35% vs middle 20-65% of audio).
        </div>
      </div>
    </div>
  );
};

export default TTSTemperaturePage;
